import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheck, FaSearch, FaDownload } from "react-icons/fa";

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getQueryTagIds = (q) => {
  const fromObjects = (arr, key = "id") =>
    (arr || []).map((o) => toNum(o?.[key])).filter((n) => n !== null);

  if (Array.isArray(q?.Tags)) return fromObjects(q.Tags, "id");
  if (Array.isArray(q?.tags)) return fromObjects(q.tags, "id");
  if (Array.isArray(q?.QueryTags)) return fromObjects(q.QueryTags, "TagId");
  if (Array.isArray(q?.tagIds))
    return (q.tagIds || []).map(toNum).filter((n) => n !== null);
  if (Array.isArray(q?.tagsIds))
    return (q.tagsIds || []).map(toNum).filter((n) => n !== null);
  return [];
};

export default function CompareQueriesPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedQueries = state?.queries || [];
  const allTags = state?.allTags || [];

  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("all"); // all | common | diff
  const [rowDensity, setRowDensity] = useState("comfortable"); // compact | comfortable

  const tagById = useMemo(() => {
    const map = new Map();
    (allTags || []).forEach((t) => map.set(Number(t.id), t));
    return map;
  }, [allTags]);

  const queryCols = useMemo(
    () =>
      selectedQueries.slice(0, 3).map((q) => ({
        id: q.id,
        name: q.name,
        tagSet: new Set(getQueryTagIds(q)),
      })),
    [selectedQueries]
  );

  // >> NOVO: todas as tags disponíveis (não só a união das consultas)
  const allTagIds = useMemo(
    () => (allTags || []).map((t) => toNum(t?.id)).filter((n) => n !== null),
    [allTags]
  );

  const rows = useMemo(() => {
    const base = allTagIds
      .map((id) => {
        const tag = tagById.get(id);
        const present = queryCols.map((col) => col.tagSet.has(id));
        const count = present.filter(Boolean).length; // em quantas consultas aparece
        return { id, name: tag?.name || `#${id}`, present, count };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const filtered =
      search.trim() === ""
        ? base
        : base.filter((r) => normalize(r.name).includes(normalize(search)));

    if (viewMode === "common")
      return filtered.filter((r) => r.count === queryCols.length);
    if (viewMode === "diff")
      return filtered.filter((r) => r.count >= 1 && r.count < queryCols.length);
    return filtered;
  }, [allTagIds, tagById, queryCols, search, viewMode]);

  const counts = useMemo(() => {
    const total = allTagIds.length;
    const common = allTagIds.filter((id) =>
      queryCols.every((c) => c.tagSet.has(id))
    ).length;
    const partial = allTagIds.filter((id) => {
      const hits = queryCols.reduce(
        (acc, c) => acc + (c.tagSet.has(id) ? 1 : 0),
        0
      );
      return hits > 0 && hits < queryCols.length;
    }).length;
    // “diffs” = presentes em pelo menos 1 e não em todas
    const diffs = partial;
    const perCol = queryCols.map((c) => c.tagSet.size);
    return { total, common, diffs, perCol };
  }, [allTagIds, queryCols]);

  const exportCSV = () => {
    const headers = ["Campo", ...queryCols.map((q) => q.name)];
    const lines = [headers];

    rows.forEach((r) => {
      lines.push([escapeCsv(r.name), ...r.present.map((p) => (p ? "1" : "0"))]);
    });

    const csv = lines.map((line) => line.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "comparativo-consultas.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedQueries.length) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900"
        >
          <FaArrowLeft /> Voltar
        </button>
        <div className="mt-6 text-gray-700">
          Nenhuma consulta selecionada para comparação.
        </div>
      </div>
    );
  }

  const padY = rowDensity === "compact" ? "py-1.5" : "py-2.5";

  return (
    <div className="w-full px-2 md:px-10 pt-4 pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Comparar Consultas
          </h1>
          <div className="w-20 h-[3px] bg-gradient-to-r from-blue-600 to-green-400 mt-2 rounded" />
          <p className="text-sm text-gray-600 mt-3">
            Visualize lado a lado os campos retornados e destaque o que é comum
            ou exclusivo em cada consulta.
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
            >
              <FaArrowLeft /> Voltar
            </button>
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
              title="Exportar CSV"
            >
              <FaDownload /> Exportar
            </button>
          </div>

          {/* Resumo */}
          <div className="flex items-center gap-2 text-[12px] text-gray-600">
            <Badge tone="neutral">Total: {counts.total}</Badge>
            <Badge tone="success">Comuns: {counts.common}</Badge>
            <Badge tone="warning">Diferentes: {counts.diffs}</Badge>
          </div>
        </div>
      </div>

      {/* Controles */}
      <div className="mt-5 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative w-full md:max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar campo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/30"
          />
        </div>

        <div className="flex items-center gap-2">
          <ToggleButton
            active={viewMode === "all"}
            onClick={() => setViewMode("all")}
            label={`Todos (${counts.total})`}
          />
          <ToggleButton
            active={viewMode === "common"}
            onClick={() => setViewMode("common")}
            label={`Comuns (${counts.common})`}
          />
          <ToggleButton
            active={viewMode === "diff"}
            onClick={() => setViewMode("diff")}
            label={`Diferentes (${counts.diffs})`}
          />
        </div>
      </div>

      {/* Tabela com header e primeira coluna “sticky” */}
      <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-auto">
          <table className="min-w-[760px] w-full border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-20">
              <tr className="border-b border-gray-200">
                <th
                  className="text-left text-[12px] font-semibold text-gray-700 px-3 py-3 sticky left-0 bg-gray-50 z-30"
                  style={{ minWidth: 240 }}
                >
                  Campo retornado
                </th>
                {queryCols.map((q) => (
                  <th
                    key={q.id}
                    className="text-center text-[12px] font-semibold text-gray-700 px-3 py-3"
                    title={q.name}
                  >
                    <div className="truncate">{q.name}</div>
                  </th>
                ))}
              </tr>

              {/* Linha de subtítulo/contagem por consulta */}
              <tr className="border-b border-gray-200">
                <th className="sticky left-0 bg-gray-50 z-30 text-left text-[11px] text-gray-500 px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    <LegendSwatch className="bg-blue-600" /> presente
                  </span>
                </th>
                {queryCols.map((q, idx) => (
                  <th
                    key={q.id}
                    className="text-center text-[11px] text-gray-600 px-3 py-2"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Badge tone="neutral">{counts.perCol[idx]} campos</Badge>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((r, ri) => {
                const isCommon = r.count === queryCols.length;
                const isDiff = r.count > 0 && r.count < queryCols.length;

                // realce sutil conforme o filtro
                const rowBg =
                  viewMode === "common" && isCommon
                    ? "bg-green-50/70"
                    : viewMode === "diff" && isDiff
                    ? "bg-amber-50/70"
                    : ri % 2 === 0
                    ? "bg-white"
                    : "bg-gray-50";

                return (
                  <tr
                    key={r.id}
                    className={`${rowBg} hover:bg-blue-50/60 transition-colors`}
                  >
                    <td
                      className={`sticky left-0 z-10 ${padY} px-3 text-[12px] text-gray-800 border-b border-gray-100 bg-inherit`}
                      style={{ minWidth: 240 }}
                      title={r.name}
                    >
                      <div className="flex items-center gap-2">
                        {isCommon ? (
                          <Badge tone="success" title="Comum a todas">
                            Comum
                          </Badge>
                        ) : isDiff ? (
                          <Badge tone="warning" title="Não aparece em todas">
                            Difere
                          </Badge>
                        ) : (
                          <span className="w-[52px]" />
                        )}
                        <span className="break-words">{r.name}</span>
                      </div>
                    </td>

                    {queryCols.map((_, i) => (
                      <td
                        key={`${r.id}-${i}`}
                        className={`${padY} px-3 text-center border-b border-gray-100`}
                        title={r.present[i] ? "Presente" : "Ausente"}
                      >
                        {r.present[i] ? (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-blue-600 to-green-400 text-white shadow-sm">
                            <FaCheck className="scale-90" />
                          </span>
                        ) : (
                          <span className="text-[12px] text-gray-400">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {!rows.length && (
                <tr>
                  <td
                    colSpan={1 + queryCols.length}
                    className="px-3 py-8 text-center text-sm text-gray-500"
                  >
                    Nenhum resultado com os filtros atuais.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Rodapé da tabela */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 text-[12px] text-gray-500">
          <div className="flex items-center gap-4">
            <LegendItem
              color="bg-gradient-to-r from-blue-600 to-green-400"
              label="Campo presente"
            />
            <LegendItem
              color="bg-green-500"
              label="Linha comum (todas)"
              swatchClass="bg-green-500"
            />
            <LegendItem
              color="bg-amber-500"
              label="Linha diferente (parcial)"
              swatchClass="bg-amber-500"
            />
          </div>
          <div>
            Exibindo <strong>{rows.length}</strong> de{" "}
            <strong>{counts.total}</strong> campos possíveis
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- UI helpers ---------- */

function ToggleButton({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 whitespace-nowrap py-1.5 text-sm rounded-lg border transition ${
        active
          ? "bg-gradient-to-r from-blue-600 to-green-400 text-white border-transparent shadow"
          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );
}

function Badge({ children, tone = "neutral", title }) {
  const tones = {
    neutral: "bg-gray-100 text-gray-700 border border-gray-200 shadow-sm",
    success: "bg-green-50 text-green-700 border border-green-200 shadow-sm",
    warning: "bg-amber-50 text-amber-700 border border-amber-200 shadow-sm",
  };
  return (
    <span
      title={title}
      className={`text-[11px] px-2 py-[2px] rounded-full inline-flex items-center ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function LegendSwatch({ className = "" }) {
  return (
    <span
      className={`inline-block w-3 h-3 rounded-sm border border-black/10 ${className}`}
    />
  );
}

function LegendItem({ color, label, swatchClass }) {
  return (
    <span className="inline-flex items-center gap-2">
      <LegendSwatch className={swatchClass || color} />
      <span>{label}</span>
    </span>
  );
}

function escapeCsv(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
